// @ts-nocheck

import { RoutePatterns } from '../../types/routes';
import type { UserAccountPage } from '../../types/user-account';
import { type AnyObject } from '../../types/utils';

// #region Utils
const Prefixes = {
  MATCH: 'match',
  CALENDAR: 'calendar',
  SPORTS_MENU: 'menu',
  MORE: 'more',
  SPORTS: 'sports',
  LIVE: 'live',
  FAVORITES: 'favorites',
  HISTORY: 'history',
  MY_MATCH: 'mymatch',
  MULTIPLEX: 'multiplex',
  GRIDS: 'grilles',
  KL: 'koh-lanta',
  EXTRAS: 'extras',
  GR: 'gift-race',
  MP: 'monopoly',
  BOOM: 'boom',
  // SHARE: 'share',
  BETSHARE: 'betshare',
  SHARE_SETTINGS: 'share-settings',
};

const getSportSubRoute = (sportId?: number | string | null) => {
  const castedSportId = parseInt(sportId, 10);

  if (!Number.isNaN(castedSportId)) {
    return `${castedSportId}`;
  }

  return null;
};

const getCategorySubRoute = (sportId?: number | string | null, categoryId?: number | string | null) => {
  const sportRoute = getSportSubRoute(sportId);
  const castedCategoryId = parseInt(categoryId, 10);

  if (sportRoute && !Number.isNaN(castedCategoryId)) {
    return `${sportRoute}/${castedCategoryId}`;
  }

  return null;
};

const getTournamentSubRoute = (
  sportId?: number | string | null,
  categoryId?: number | string | null,
  tournamentId?: number | string | null,
) => {
  const categoryRoute = getCategorySubRoute(sportId, categoryId);
  const castedTournamentId = parseInt(tournamentId, 10);

  if (categoryRoute && !Number.isNaN(castedTournamentId)) {
    return `${categoryRoute}/${castedTournamentId}`;
  }

  return null;
};
// #endregion

// #region Pathname factory methods implementation
const getMatchRoutePathname = (matchId: number | string) => {
  const castedMatchId = parseInt(matchId, 10);

  if (!Number.isNaN(castedMatchId)) {
    return `/${Prefixes.MATCH}/${castedMatchId}`;
  }

  return null;
};

const getMatchHighlightRoutePathname = (matchId: number | string) => {
  const castedMatchId = parseInt(matchId, 10);

  return `/${Prefixes.MATCH}/${castedMatchId}`;
};

const getCalendarRoutePathname = () => {
  return `/${Prefixes.CALENDAR}`;
};

const getSportsMenuRoutePathname = () => {
  return `/${Prefixes.SPORTS_MENU}`;
};

const getCatalogRoutePathname = (
  sportId: number | string,
  categoryId?: number | string | null,
  tournamentId?: number | string | null,
) => {
  const tournamentRoute = getTournamentSubRoute(sportId, categoryId, tournamentId);

  if (tournamentRoute) {
    return `/${Prefixes.SPORTS}/${tournamentRoute}`;
  }

  const categoryRoute = getCategorySubRoute(sportId, categoryId);

  if (categoryRoute) {
    return `/${Prefixes.SPORTS}/${categoryRoute}`;
  }

  const sportRoute = getSportSubRoute(sportId);

  if (sportRoute) {
    return `/${Prefixes.SPORTS}/${sportRoute}`;
  }

  return null;
};

const getHistoryRoutePathname = (historyFilterStatusId?: number | string | null) => {
  const castedFilterId = parseInt(historyFilterStatusId, 10);
  const suffixFilterId = !Number.isNaN(castedFilterId) ? `/${castedFilterId}` : '';

  return `/${Prefixes.HISTORY}${suffixFilterId}`;
};

const getHistoryShareBetslipRoutePathname = (betslipReference: string) => {
  return `/#share=${betslipReference}`;
};

const getHistoryBetslipDetailsRoutePathname = (betslipReference: string) => {
  if (betslipReference) {
    return `/${Prefixes.HISTORY}/r/${betslipReference}`;
  }
};

const getFavoritesRoutePathname = () => {
  return `/${Prefixes.FAVORITES}`;
};

const getMyMatchRoutePathname = (matchId: number | string) => {
  const castedMatchId = parseInt(matchId, 10);

  if (!Number.isNaN(castedMatchId)) {
    return `/${Prefixes.MY_MATCH}/${castedMatchId}`;
  }

  return null;
};

const getHomeRoutePathname = () => {
  return `/`;
};

const getLiveRoutePathname = (
  sportId?: number | string | null,
  categoryId?: number | string | null,
  tournamentId?: number | string | null,
) => {
  const tournamentRoute = getTournamentSubRoute(sportId, categoryId, tournamentId);

  if (tournamentRoute) {
    return `/${Prefixes.LIVE}/${tournamentRoute}`;
  }

  const categoryRoute = getCategorySubRoute(sportId, categoryId);

  if (categoryRoute) {
    return `/${Prefixes.LIVE}/${categoryRoute}`;
  }

  const sportRoute = getSportSubRoute(sportId);

  if (sportRoute) {
    return `/${Prefixes.LIVE}/${sportRoute}`;
  }

  return `/${Prefixes.LIVE}`;
};

const getMultiviewRoutePathname = () => {
  return `/${Prefixes.MULTIPLEX}`;
};

const getGridsRoutePathname = () => {
  return `/${Prefixes.GRIDS}`;
};

const getGridsPoolRoutePathname = (poolTitle?: string) => {
  const poolTitleSuffix = poolTitle ? `/${poolTitle}` : '';

  return `/${Prefixes.GRIDS}${poolTitleSuffix}`;
};

const getGridsHistoryRoutePathname = () => {
  return `/${Prefixes.GRIDS}/history`;
};

const getGridsGridHistoryDetailsRoutePathname = (gridReference: string) => {
  return `/${Prefixes.GRIDS}/history/${gridReference}`;
};

const getGridsGridShareRoutePathname = (gridReference: string) => {
  return `/${Prefixes.GRIDS}/share/${gridReference}`;
};

const getGridsPublicGridsRoutePathname = (poolTitle: string, gridReference?: string) => {
  const gridReferenceSuffix = gridReference ? `/${gridReference}` : '';
  return `/grilles/${poolTitle}/public${gridReferenceSuffix}`;
};

const getKLRoutePathname = () => {
  return `/${Prefixes.KL}`;
};

const getKLEnrollementRoutePathname = () => {
  return `/${Prefixes.KL}/enrollment`;
};

const getKLCollectibleDetailsRoutePathname = (obfuscatedCollectibleId: number) => {
  return `/${Prefixes.KL}/badge-details/${obfuscatedCollectibleId}`;
};

const getKLRoundRulesRoutePathname = (obfuscatedRoundId: number) => {
  return `/${Prefixes.KL}/round-rules/${obfuscatedRoundId}`;
};

const getKLOrientationStepDetailsRoutePathname = (progressIndex: number, obfuscatedStepId: number) => {
  return `/${Prefixes.KL}/orientation-step/${progressIndex}-${obfuscatedStepId}`;
};

const getExtrasRoutePathname = () => {
  return `/${Prefixes.EXTRAS}`;
};

const getGRRoutePathname = () => {
  return `/${Prefixes.GR}`;
};

const getGREnrollementRoutePathname = () => {
  return `/${Prefixes.GR}/enrollment`;
};

const getGRRoundRulesRoutePathname = (obfuscatedRoundId: number) => {
  return `/${Prefixes.GR}/round-rules/${obfuscatedRoundId}`;
};

const getMPRoutePathname = () => {
  return `/${Prefixes.MP}`;
};

const getMPEnrollementRoutePathname = () => {
  return `/${Prefixes.MP}/enrollment`;
};

const getMPFamilyDetailRoutePathname = (familyId: string) => {
  return `/${Prefixes.MP}/family/${familyId}`;
};

const getLineUpRoutePathname = (matchId: number) => {
  return `/${Prefixes.MATCH}/${matchId}/line-up`;
};

const getBoomRoutePathname = () => {
  return `/${Prefixes.BOOM}`;
};

const getBoomEnrollementRoutePathname = () => {
  return `/${Prefixes.BOOM}/enrollment`;
};

const getBoomStepRoutePathname = (stepId: string) => {
  return `/${Prefixes.BOOM}/step/${stepId}`;
};

// #endregion

const getGraphicsPreferencesRoutePathname = () => {
  return RoutePatterns.GRAPHICS_PREFERENCES;
};

// #region Config factory methods implementation
const getMatchRouteConfig = (matchId: number | string, options?: AnyObject = {}) => {
  return {
    pathname: getMatchRoutePathname(matchId),
    ...options,
  };
};

const getMatchHighlightRouteConfig = (matchId: number | string, highlightId: string, options?: AnyObject = {}) => {
  return {
    pathname: getMatchHighlightRoutePathname(matchId),
    search: `?highlightId=${highlightId}`,
    ...options,
  };
};

const getCalendarRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getCalendarRoutePathname(),
    ...options,
  };
};

const getSportsMenuRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getSportsMenuRoutePathname(),
    ...options,
  };
};

const getCatalogRouteConfig = (
  sportId: number | string,
  categoryId?: number | string | null,
  tournamentId?: number | string | null,
  options?: AnyObject = {},
) => {
  return {
    pathname: getCatalogRoutePathname(sportId, categoryId, tournamentId),
    ...options,
  };
};

const getHistoryRouteConfig = (historyFilterStatusId?: number | string | null, options?: AnyObject = {}) => {
  return {
    pathname: getHistoryRoutePathname(historyFilterStatusId),
    ...options,
  };
};

const getHistoryShareBetslipRouteConfig = (betslipReference: string, options?: AnyObject = {}) => {
  return {
    pathname: getHistoryShareBetslipRoutePathname(betslipReference),
    ...options,
  };
};

const getHistoryBetslipDetailsRouteConfig = (betslipReference: string, options?: AnyObject = {}) => {
  return {
    pathname: getHistoryBetslipDetailsRoutePathname(betslipReference),
    ...options,
  };
};

const getFavoritesRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getFavoritesRoutePathname(),
    ...options,
  };
};

const getMyMatchRouteConfig = (matchId: number | string, options?: AnyObject = {}) => {
  return {
    pathname: getMyMatchRoutePathname(matchId),
    ...options,
  };
};

const getHomeRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getHomeRoutePathname(),
    ...options,
  };
};

const getExtrasRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getExtrasRoutePathname(),
    ...options,
  };
};

const getLiveRouteConfig = (
  sportId?: number | string | null,
  categoryId?: number | string | null,
  tournamentId?: number | string | null,
  options?: AnyObject = {},
) => {
  return {
    pathname: getLiveRoutePathname(sportId, categoryId, tournamentId),
    ...options,
  };
};

const getMultiviewRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getMultiviewRoutePathname(),
    ...options,
  };
};

const getGridsRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getGridsRoutePathname(),
    ...options,
  };
};

const getGridsPoolRouteConfig = (poolTitle?: string, options?: AnyObject = {}) => {
  return {
    pathname: getGridsPoolRoutePathname(poolTitle),
    ...options,
  };
};

const getGridsHistoryRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getGridsHistoryRoutePathname(),
    ...options,
  };
};

const getGridsGridHistoryDetailsRouteConfig = (gridReference: string, options?: AnyObject = {}) => {
  return {
    pathname: getGridsGridHistoryDetailsRoutePathname(gridReference),
    ...options,
  };
};

const getGridsGridShareRouteConfig = (gridReference: string, options?: AnyObject = {}) => {
  return {
    pathname: getGridsGridShareRoutePathname(gridReference),
    ...options,
  };
};

const getGridsPublicGridsRouteConfig = (poolTitle: string, gridReference?: string, options?: AnyObject = {}) => {
  return {
    pathname: getGridsPublicGridsRoutePathname(poolTitle, gridReference),
    ...options,
  };
};

const getKLRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getKLRoutePathname(),
    ...options,
  };
};

const getKLEnrollementRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getKLEnrollementRoutePathname(),
    ...options,
  };
};

const getKLCollectibleDetailsRouteConfig = (obfuscatedCollectibleId: number, options?: AnyObject = {}) => {
  return {
    pathname: getKLCollectibleDetailsRoutePathname(obfuscatedCollectibleId),
    ...options,
  };
};

const getKLRoundRulesRouteConfig = (obfuscatedRoundId: number, options?: AnyObject = {}) => {
  return {
    pathname: getKLRoundRulesRoutePathname(obfuscatedRoundId),
    ...options,
  };
};

const getKLOrientationStepDetailsRouteConfig = (
  progressIndex: number,
  obfuscatedStepId: number,
  options?: AnyObject = {},
) => {
  return {
    pathname: getKLOrientationStepDetailsRoutePathname(progressIndex, obfuscatedStepId),
    ...options,
  };
};

const getGRRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getGRRoutePathname(),
    ...options,
  };
};

const getGREnrollementRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getGREnrollementRoutePathname(),
    ...options,
  };
};

const getGRRoundRulesRouteConfig = (obfuscatedRoundId: number, options?: AnyObject = {}) => {
  return {
    pathname: getGRRoundRulesRoutePathname(obfuscatedRoundId),
    ...options,
  };
};

const getMPRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getMPRoutePathname(),
    ...options,
  };
};

const getMPEnrollementRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getMPEnrollementRoutePathname(),
    ...options,
  };
};

const getMPFamilyDetailRouteConfig = (familyId: string, options?: AnyObject = {}) => {
  return {
    pathname: getMPFamilyDetailRoutePathname(familyId),
    ...options,
  };
};

const getLineUpRouteConfig = (matchId: number, options?: AnyObject = {}) => {
  return {
    pathname: getLineUpRoutePathname(matchId),
    ...options,
  };
};

const getBasketRouteConfig = (options?: AnyObject = {}) => {
  return {
    hash: '#cart',
    ...options,
  };
};

const getMultiviewEventListRouteConfig = (options?: AnyObject = {}) => {
  return {
    hash: '#multiplexEventList',
    ...options,
  };
};

const getLoginRouteConfig = (options?: AnyObject = {}) => {
  return {
    hash: '#login',
    ...options,
  };
};

const getUserAccountRouteConfig = (subpath: UserAccountPage | null, options?: AnyObject = {}) => {
  const subpathSuffix = subpath ? `/${subpath}` : '';

  return {
    hash: `#user-account${subpathSuffix}`,
    ...options,
  };
};

const getScannerRouteConfig = (options?: AnyObject = {}) => {
  return {
    hash: '#scanner',
    ...options,
  };
};

const getCupRosterRouteConfig = (options?: AnyObject = {}) => {
  return {
    hash: '#cupRoster',
    ...options,
  };
};

const getLiveTableRouteConfig = (options?: AnyObject = {}) => {
  return {
    hash: 'liveTable',
    ...options,
  };
};

const getGraphicsPreferencesRouteConfig = () => {
  return {
    pathname: getGraphicsPreferencesRoutePathname(),
  };
};

const getBetshareRouteConfig = (shortURLId: string, ref: string, options?: AnyObject = {}) => {
  return {
    hash: `betshare/${ref}`,
    search: `?shortURLId=${shortURLId}`,
    ...options,
  };
};

const getMissionsDetailsRouteConfig = (missionId: number, options?: AnyObject = {}) => {
  return {
    pathname: getExtrasRoutePathname(),
    hash: `mission-details-${missionId}`,
    ...options,
  };
};

const getBoomRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getBoomRoutePathname(),
    ...options,
  };
};

const getBoomEnrollementRouteConfig = (options?: AnyObject = {}) => {
  return {
    pathname: getBoomEnrollementRoutePathname(),
    ...options,
  };
};

const getBoomStepRouteConfig = (stepId: string, options?: AnyObject = {}) => {
  return {
    pathname: getBoomStepRoutePathname(stepId),
    ...options,
  };
};

// #endregion

const RoutesRedirectionFactory = {
  getMatchRouteConfig,
  getMatchRoutePathname,

  getMatchHighlightRouteConfig,
  getMatchHighlightRoutePathname,

  getCalendarRouteConfig,
  getCalendarRoutePathname,

  getCatalogRouteConfig,
  getCatalogRoutePathname,

  getSportsMenuRouteConfig,
  getSportsMenuRoutePathname,

  getHistoryRouteConfig,
  getHistoryRoutePathname,
  getHistoryShareBetslipRouteConfig,
  getHistoryShareBetslipRoutePathname,
  getHistoryBetslipDetailsRouteConfig,
  getHistoryBetslipDetailsRoutePathname,

  getFavoritesRouteConfig,
  getFavoritesRoutePathname,

  getBasketRouteConfig,

  getMyMatchRouteConfig,
  getMyMatchRoutePathname,

  getHomeRouteConfig,
  getHomeRoutePathname,

  getLiveRouteConfig,
  getLiveRoutePathname,

  getMultiviewRouteConfig,
  getMultiviewRoutePathname,
  getMultiviewEventListRouteConfig,

  getGridsRouteConfig,
  getGridsRoutePathname,
  getGridsPoolRouteConfig,
  getGridsPoolRoutePathname,
  getGridsHistoryRouteConfig,
  getGridsHistoryRoutePathname,
  getGridsGridHistoryDetailsRouteConfig,
  getGridsGridHistoryDetailsRoutePathname,
  getGridsGridShareRouteConfig,
  getGridsGridShareRoutePathname,
  getGridsPublicGridsRouteConfig,
  getGridsPublicGridsRoutePathname,

  getKLRouteConfig,
  getKLRoutePathname,
  getKLEnrollementRouteConfig,
  getKLEnrollementRoutePathname,
  getKLCollectibleDetailsRouteConfig,
  getKLCollectibleDetailsRoutePathname,
  getKLRoundRulesRouteConfig,
  getKLRoundRulesRoutePathname,
  getKLOrientationStepDetailsRouteConfig,
  getKLOrientationStepDetailsRoutePathname,

  getLoginRouteConfig,

  getUserAccountRouteConfig,

  getScannerRouteConfig,

  getExtrasRoutePathname,
  getExtrasRouteConfig,
  getMissionsDetailsRouteConfig,

  getGRRoutePathname,
  getGRRouteConfig,
  getGREnrollementRoutePathname,
  getGREnrollementRouteConfig,
  getGRRoundRulesRoutePathname,
  getGRRoundRulesRouteConfig,

  getLiveTableRouteConfig,
  getCupRosterRouteConfig,

  getLineUpRouteConfig,

  getMPRouteConfig,
  getMPRoutePathname,
  getMPEnrollementRouteConfig,
  getMPEnrollementRoutePathname,
  getMPFamilyDetailRouteConfig,
  getMPFamilyDetailRoutePathname,

  getGraphicsPreferencesRouteConfig,

  getBoomRouteConfig,
  getBoomRoutePathname,
  getBoomEnrollementRouteConfig,
  getBoomEnrollementRoutePathname,
  getBoomStepRouteConfig,
  getBoomStepRoutePathname,

  getBetshareRouteConfig,
};

export default RoutesRedirectionFactory;
